import { useState, type FC } from 'react'
import { useTranslation } from 'react-i18next'
import useSpeechDialogStore from './speech-dialog.store.ts'
import Dialog from '@/components/common/Dialog'
import VoiceSettings from './VoiceSettings'
import ScriptForm from './ScriptForm'
import SpeechPreview from './SpeechPreview'
import Icon from '../common/Icon'
import { createScript } from '@/features/script/domain/use-cases/create-script'
import { updateSpeechClip } from '@/features/clips/domain/use-cases/update-speech-clip'
import { updateVoiceClip } from '@/features/clips/domain/use-cases/update-voice-clip'
import { addVoiceClip } from '@/features/clips/domain/use-cases/add-voice-clip'
import type { AudioFileSource } from '@/features/media-file/domain/models/media-file.ts'
import addSpeechClip from '@/features/clips/domain/use-cases/add-speech-clip'
import useUiStore from '@/shared/store/ui.store.tsx'
import { updateScript } from '@/features/script/domain/use-cases/update-script.ts'

const SpeechDialogMain: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const { setSpeechDialogOpen } = useUiStore()
  const {
    expressive,
    script,
    speed,
    scriptText,
    audioFiles,
    selectedAudioFileId,
    selectedVoice,
    validateScriptText,
    selectedTab,
    validateAudioFiles,
  } = useSpeechDialogStore()
  const selectedFile = audioFiles.find(
    (file) => file.id === selectedAudioFileId
  )
  const { editingClip } = useSpeechDialogStore()
  const isEditing = !!editingClip
  const mode = selectedTab === 'text' ? 'tts' : 'sts'

  const title = isEditing
    ? t('ls_generic_edit_speech')
    : t('ls_generic_generate_speech')

  const updateOrCreateScript = () => {
    const scriptId = script?.id
    if (scriptId) {
      const updatedScript = updateScript(scriptId, scriptText)
      if (updatedScript.ok) return updatedScript.data
      console.error('Failed to update script', updatedScript.error)
      return
    }
    const createdScript = createScript(scriptText)
    if (createdScript.ok) return createdScript.data
    console.error('Failed to create script', createdScript.error)
    return
  }

  const generateSpeech = async (): Promise<boolean> => {
    if (mode === 'tts' && !validateScriptText(scriptText)) return false
    if (!selectedVoice) return false
    if (mode === 'sts' && !validateAudioFiles()) return false

    if (mode === 'tts') {
      const storedScript = updateOrCreateScript()
      if (!storedScript) return false

      setSpeechDialogOpen(false)
      if (isEditing) {
        await updateVoiceClip(
          editingClip.id,
          selectedVoice,
          storedScript,
          speed,
          expressive
        )
      } else {
        await addVoiceClip(selectedVoice, storedScript, speed, expressive)
      }
      return true
    }

    // STS mode — no script creation (placeholder ID used for local bookkeeping)
    setSpeechDialogOpen(false)
    const scriptIdPlaceholder = ''
    if (isEditing) {
      await updateSpeechClip(
        editingClip.id,
        scriptIdPlaceholder,
        selectedFile as AudioFileSource,
        selectedVoice,
        expressive
      )
    } else {
      await addSpeechClip(
        scriptIdPlaceholder,
        selectedFile as AudioFileSource,
        selectedVoice,
        expressive
      )
    }
    return true
  }

  const onSubmit = async () => {
    setBusy(true)
    const success = await generateSpeech()
    setBusy(false)
    if (!success) return
    onClose()
  }

  return (
    <>
      <Dialog.Header onClose={onClose}>
        <span className="text-[16px] font-semibold">{title}</span>
      </Dialog.Header>
      <div className="flex flex-1 flex-row">
        <ScriptForm />
        <VoiceSettings />
      </div>
      <Dialog.Footer
        onCancelClick={onClose}
        confirmButton={
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded bg-blue-800 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600/75 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-800"
            disabled={busy}
            onClick={onSubmit}
          >
            <span
              className={`flex flex-row items-center gap-2 ${
                busy ? 'animate-pulse' : ''
              }`}
            >
              <Icon name="sparkles" />
            </span>
            <span>
              {busy
                ? t('ls_generic_generating')
                : isEditing
                  ? t('ls_generic_update')
                  : t('ls_generic_generate')}
            </span>
          </button>
        }
      >
        <div className="flex w-full flex-row">
          <SpeechPreview />
        </div>
      </Dialog.Footer>
    </>
  )
}

export default SpeechDialogMain
